import {AmaronLeadCategoryEnum} from "../../../workspaceSpecific/domain/amaron/EventContainers";
import type {Integer, Iso8601Date, Iso8601DateTime, Uuid} from "@vesta/common-type-definitions/typeDefinitions";
import {
    integerZodValidator,
    iso8601DateTimeZodValidator,
    iso8601DateZodValidator,
    uuidZodValidator,
} from "@vesta/global-common-typescript/validators/zodValidators";
import {z as zod} from "zod";

/** Queue categories use the same UUIDs as the persisted lead and day-plan category. */
export type AgentViewQueueDisplayGroup = AmaronLeadCategoryEnum;
export const agentViewQueueDisplayGroupValidator = zod.enum(AmaronLeadCategoryEnum);

export enum AgentDayPlanItemQueueMode {
    planned = "2d98209f-7af5-417b-97e4-58d6ab6d72e0",
    dynamic = "6b92d5d7-31f9-4cc9-8ac2-18f035c54a34",
}

export enum AgentDayPlanItemQueueReason {
    opportunity = "2369cfbb-ae9d-4395-ac0b-b0e945c2a08d",
    callback = "5a993dc0-9177-49c4-a8c1-a00e2097f973",
    newLead = "08b02144-8f52-4fe8-a0e1-628f16d4cc35",
}

export enum AgentDayPlanItemStatus {
    pending = "ea436055-2a2b-45c6-9876-4be39ee37eb9",
    completed = "65dcd101-ddc3-41e2-877f-02bcb6de82e7",
}

export type AgentCapacity = {
    capacity: Integer;
    createdAt: Iso8601DateTime;
    id: Uuid;
    updatedAt: Iso8601DateTime;
    userId: Uuid;
};

export type AgentDayPlanItem = {
    agentUserId: Uuid;
    assignedOn: Iso8601DateTime | null;
    completedAt: Iso8601DateTime | null;
    completedDisposition: Uuid | null;
    createdAt: Iso8601DateTime;
    disposition: Uuid;
    eventContainerId: Uuid;
    id: Uuid;
    leadCategory: Uuid;
    planDate: Iso8601Date;
    queueMode: AgentDayPlanItemQueueMode;
    queueReason: AgentDayPlanItemQueueReason | null;
    scheduledOn: Iso8601DateTime | null;
    slaBreachedAt: Iso8601DateTime | null;
    sourcePriority: Integer;
    status: AgentDayPlanItemStatus;
    updatedAt: Iso8601DateTime;
};

export const agentCapacityValidator: zod.ZodType<AgentCapacity> = zod.strictObject({
    capacity: integerZodValidator,
    createdAt: iso8601DateTimeZodValidator,
    id: uuidZodValidator,
    updatedAt: iso8601DateTimeZodValidator,
    userId: uuidZodValidator,
});

export const agentDayPlanItemValidator: zod.ZodType<AgentDayPlanItem> = zod.strictObject({
    agentUserId: uuidZodValidator,
    assignedOn: iso8601DateTimeZodValidator.nullable(),
    completedAt: iso8601DateTimeZodValidator.nullable(),
    completedDisposition: uuidZodValidator.nullable(),
    createdAt: iso8601DateTimeZodValidator,
    disposition: uuidZodValidator,
    eventContainerId: uuidZodValidator,
    id: uuidZodValidator,
    leadCategory: uuidZodValidator,
    planDate: iso8601DateZodValidator,
    queueMode: zod.enum(AgentDayPlanItemQueueMode),
    queueReason: zod.enum(AgentDayPlanItemQueueReason).nullable(),
    scheduledOn: iso8601DateTimeZodValidator.nullable(),
    slaBreachedAt: iso8601DateTimeZodValidator.nullable(),
    sourcePriority: integerZodValidator,
    status: zod.enum(AgentDayPlanItemStatus),
    updatedAt: iso8601DateTimeZodValidator,
});
